import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { NO_TEAM, NO_TEAM_string, UserAccessRight, userFullName } from '../../../general/model';
import { BLOptions } from '../../../general/modelCreativeFolder';
import { CHNodeCheckName, CHNodeIsCreativeFolder } from '../../../general/node';
import { misleadSingleton } from '../app/misleadSingleton';
import { RootSelector } from '../../models/rootSelector';
import { getPermissionsIndexed, getUsers } from '../app/selectors';
import { getSelectedNodesInAllFolders, getTeams } from '../fileManager/selectors/selectors';
import {
  AI_GENERATED_OPTIONS,
  CreativeFolderShape,
  MultipleValues,
  MultipleValuesSymbol,
  WithMultipleValues,
} from './model';

// Опции всех выпадающих списков модуля — в формате kit `Select`: {label, value}.
const MultipleValuesOption = {
  label: MultipleValuesSymbol.description,
  value: MultipleValuesSymbol.description,
};

export function useMisleadOptions() {
  const currentMislead = useSelector(getBLOptions).mislead_level;
  const misleads = misleadSingleton.getAll();
  const misleadOptions = misleads.map((level) => ({
    label: level.name,
    value: level.id,
  }));
  return currentMislead === MultipleValuesSymbol
    ? [MultipleValuesOption, ...misleadOptions]
    : misleadOptions;
}

const isMultipleValues = function <T>(value: T | MultipleValues): value is MultipleValues {
  return value === MultipleValuesSymbol;
};

export const getCreativeFolder: RootSelector<CreativeFolderShape> = (store) => store.creativeFolder;
export const getTeamID = createSelector(getCreativeFolder, (cf) => cf.options.teamID ?? NO_TEAM);
export const getTags = createSelector(getCreativeFolder, (cf) => cf.context.tags);
export const getTagsNew = createSelector(getCreativeFolder, (cf) => cf.meta.tagsNew);
export const getTagSearchText = createSelector(getCreativeFolder, (cf) => cf.meta.tagSearchText);

export const getTagsActive: RootSelector<string[]> = createSelector(getCreativeFolder, (cf) =>
  isMultipleValues(cf.options.tagsActive) ? [] : cf.options.tagsActive,
);

export const getBLOptions: RootSelector<WithMultipleValues<BLOptions>> = createSelector(
  [getTagsActive, getTeamID, getCreativeFolder],
  (tagsActive, teamID, cf) => {
    return {
      ...cf.options,
      tagsActive,
      teamID,
    };
  },
);

export const getProjectDropdownOptions: RootSelector<
  (
    | {
        label: string;
        value: number;
      }
    | typeof MultipleValuesOption
  )[]
> = createSelector([getCreativeFolder], (cf) => {
  const opts = cf.context.projects.map((item) => ({ label: item.name, value: item.id }));
  return cf.options.projectId === MultipleValuesSymbol ? [MultipleValuesOption, ...opts] : opts;
});
export const getGenreDropdownOptions = createSelector([getCreativeFolder], (cf) => {
  const opts = cf.context.genres.map((item) => ({
    label: item.name,
    value: item.id,
  }));
  return cf.options.genres === MultipleValuesSymbol ? [MultipleValuesOption, ...opts] : opts;
});
export const getIntroDropdownOptions = createSelector([getCreativeFolder], (cf) => {
  const opts = cf.context.intros.map((item) => ({
    label: item.name,
    value: item.id,
  }));
  return cf.options.intro === MultipleValuesSymbol ? [MultipleValuesOption, ...opts] : opts;
});
export const getAiGeneratedDropdownOptions = createSelector([getCreativeFolder], (cf) =>
  cf.options.ai_generated === MultipleValuesSymbol
    ? [MultipleValuesOption, ...AI_GENERATED_OPTIONS]
    : AI_GENERATED_OPTIONS,
);
export const getConceptTypeDropdownOptions = createSelector([getCreativeFolder], (cf) => {
  const opts = cf.context.conceptTypes.map(({ id, name }) => ({ label: name, value: id }));
  return cf.options.concept_type === MultipleValuesSymbol ? [MultipleValuesOption, ...opts] : opts;
});

export const getCreativeTypeDropdownOptions = createSelector([getCreativeFolder], (cf) => {
  const opts = cf.context.creativeTypes.map((item) => ({ label: item.name, value: item.id }));
  return cf.options.creativeTypeId === MultipleValuesSymbol
    ? [MultipleValuesOption, ...opts]
    : opts;
});

const getUsersOptions = createSelector([getUsers], (users) =>
  (users ?? []).map((user) => ({
    label: `${userFullName(user)} (${user.email})`,
    value: user,
  })),
);
const getTeamsOptions = createSelector([getTeams], (teams) => [
  ...(teams ?? []).map((team) => ({
    label: team.name,
    value: team.id,
  })),
  { label: NO_TEAM_string, value: NO_TEAM },
]);
export const getDeveloperDropdownOptions = createSelector(
  [getUsersOptions, getCreativeFolder],
  (usersOptions, cf) =>
    cf.options.developerId === MultipleValuesSymbol
      ? [MultipleValuesOption, ...usersOptions]
      : usersOptions,
);
export const getTeamDropdownOptions = createSelector(
  [getTeamsOptions, getTeamID],
  (teamsOptions, tID) =>
    tID === MultipleValuesSymbol ? [MultipleValuesOption, ...teamsOptions] : teamsOptions,
);

export const getManagerDropdownOptions = createSelector(
  [getUsersOptions, getCreativeFolder],
  (usersOptions, cf) =>
    cf.options.managerId === MultipleValuesSymbol
      ? [MultipleValuesOption, ...usersOptions]
      : usersOptions,
);

export const getTagDropdownOptions = createSelector(
  [getTags, getTagsNew, getTagSearchText],
  (tags, tagsNew, tagSearchText) => {
    const result = [...tags, ...tagsNew].map((tag) => ({
      label: tag,
      value: tag,
    }));

    if (
      tagSearchText &&
      !result.find(({ value }) => value.toLowerCase() === tagSearchText.toLowerCase())
    ) {
      result.unshift({
        label: tagSearchText,
        value: tagSearchText,
      });
    }

    return result;
  },
);

// --- AI-разметка сюжета, вкладка `Metadata` (MYUA-16423) ---

/** Значения полей разметки; `MultipleValuesSymbol` — выбрано несколько креативов. */
export const getPlotRecognition = createSelector(
  getCreativeFolder,
  (cf) => cf.options.plot_recognition,
);

export const getPlotRecognitionInfo = createSelector(
  getCreativeFolder,
  (cf) => cf.meta.plotRecognitionInfo,
);

/** Вкладка доступна на редактирование: одиночный креатив + права `admin`. */
export const getCanEditPlotRecognition = createSelector(
  getCreativeFolder,
  (cf) =>
    (cf.meta.editableFields ?? []).includes('plot_recognition') &&
    cf.options.plot_recognition !== MultipleValuesSymbol,
);

export const getConfirmIsDisabled = createSelector([getCreativeFolder], (cf) => {
  const isValidCreativeName =
    isMultipleValues(cf.options.creativeName) || CHNodeCheckName(cf.options.creativeName);

  const isDisabled =
    cf.meta.confirmInProgress ||
    !isValidCreativeName ||
    !cf.options.projectId ||
    !cf.options.creativeTypeId ||
    !cf.options.managerId ||
    !cf.options.developerId;

  return isDisabled;
});

export const getShowCreativeFoldersEditButton: RootSelector<boolean> = createSelector(
  getSelectedNodesInAllFolders,
  getPermissionsIndexed,
  (allNodes) => {
    return allNodes.every(CHNodeIsCreativeFolder) && allNodes.length > 0;
  },
);

export const getIsActiveCreativeFoldersEditButton = createSelector(
  [getSelectedNodesInAllFolders, getPermissionsIndexed],
  (allNodes, permissionsIndexed) => {
    // Check if user has the special permission to force edit creative
    const hasForceEditPermission = permissionsIndexed['bl.force_can_edit_creative'];

    // If user has the force permission, allow editing regardless of node permissions
    if (hasForceEditPermission) {
      return true;
    }

    // Otherwise, check normal write permissions on all nodes
    return allNodes.every((node) => node.perms.includes(UserAccessRight.write));
  },
);
